import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle2, XCircle, Clock, AlertCircle, Eye } from 'lucide-react';
import { aiPurchasesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { RotatingGlowButton } from '../../components/ui/RotatingGlowButton';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';
import { AIDraftReviewPanel } from '../../components/reconciliation/AIDraftReviewPanel';

const PurchaseAIReview = () => {
  const navigate = useNavigate();
  const { success, error, ToastContainer } = useToast();

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    loadDrafts();
  }, [statusFilter, pagination.page]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const result = await aiPurchasesAPI.getDrafts(params);
      setDrafts(result.drafts || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        totalPages: result.pagination?.totalPages || 0,
      }));
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetails = async (id) => {
    try {
      const result = await aiPurchasesAPI.getDraft(id);
      setSelectedDraft(result.draft);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to load draft details');
    }
  };

  const handleApprove = async (id) => {
    if (!confirm('Are you sure you want to approve this draft and create a purchase?')) {
      return;
    }
    try {
      setApproving(true);
      await aiPurchasesAPI.approve(id);
      success('Draft approved and purchase created successfully!');
      loadDrafts();
      setSelectedDraft(null);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to approve draft');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (id) => {
    const notes = prompt('Enter rejection notes (optional):');
    if (notes === null) return;

    try {
      setRejecting(true);
      await aiPurchasesAPI.reject(id, notes || '');
      success('Draft rejected successfully');
      loadDrafts();
      setSelectedDraft(null);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to reject draft');
    } finally {
      setRejecting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'needs_review':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'approved':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'needs_review':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
      case 'processing':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-h1 font-medium text-[var(--ds-text-primary)] mb-2">
              AI Purchase Drafts Review
            </h1>
            <p className="text-body text-[var(--ds-text-secondary)]">
              Review and approve AI-extracted purchase invoices
            </p>
          </div>
          <RotatingGlowButton
            variant="secondary"
            size="sm"
            onClick={() => navigate('/tenant/purchases/ai-upload')}
            className="inline-flex items-center gap-2 text-orange-500"
            glowColor="orange"
            glowSpeed={1}
          >
            Upload New Invoice
          </RotatingGlowButton>
        </div>

        {/* Status Filter */}
        <div className="mb-6 flex gap-2 flex-wrap">
          {['all', 'processing', 'completed', 'needs_review', 'failed', 'approved', 'rejected'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'primary' : 'outline'}
              size="sm"
              onClick={() => {
                setStatusFilter(status);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Button>
          ))}
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-body text-[var(--ds-text-secondary)]">Loading drafts...</p>
            </CardContent>
          </Card>
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-[var(--ds-text-secondary)]" />
              <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                No drafts found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
              </p>
              <RotatingGlowButton
                onClick={() => navigate('/tenant/purchases/ai-upload')}
                glowColor="orange"
                glowSpeed={1}
                className="text-orange-500"
              >
                Upload Your First Invoice
              </RotatingGlowButton>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Drafts List */}
            <div className="lg:col-span-2 space-y-4">
              {drafts.map(draft => (
                <Card key={draft.id} className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => loadDraftDetails(draft.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(draft.status)}
                          <h3 className="text-h3 font-medium text-[var(--ds-text-primary)]">
                            {draft.source_file_name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 text-meta text-[var(--ds-text-secondary)] mb-2">
                          <span className={`px-2 py-1 rounded ${getStatusColor(draft.status)}`}>
                            {draft.status.replace('_', ' ')}
                          </span>
                          {draft.ai_confidence && (
                            <span>Confidence: {Math.round(draft.ai_confidence * 100)}%</span>
                          )}
                        </div>
                        {draft.supplier_name && (
                          <p className="text-body text-[var(--ds-text-primary)] mb-1">
                            Supplier: <strong>{draft.supplier_name}</strong>
                          </p>
                        )}
                        <p className="text-meta text-[var(--ds-text-secondary)]">
                          Uploaded {formatDate(draft.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadDraftDetails(draft.id);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="text-body text-[var(--ds-text-secondary)]">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>

            {/* Draft Details Sidebar */}
            {selectedDraft && (
              <div className="lg:col-span-1">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">
                        Draft Details
                      </h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDraft(null)}
                      >
                        ×
                      </Button>
                    </div>

                    <AIDraftReviewPanel
                      draft={selectedDraft}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onDraftUpdated={setSelectedDraft}
                      approving={approving}
                      rejecting={rejecting}
                      toast={{ success, error }}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
      <ToastContainer />
    </Layout>
  );
};

export default PurchaseAIReview;
